/**
 * BW Trailer Hitches - Dealer Locator
 * Proximity search powered by Supabase PostGIS
 */

class DealerLocator {
  constructor(config) {
    this.supabaseUrl = config.supabaseUrl;
    this.supabaseKey = config.supabaseKey;
    this.googleMapsKey = config.googleMapsKey;
    this.perPage = config.perPage || 10;

    this.currentPage = 1;
    this.totalResults = 0;
    this.currentCoords = null;
    this.map = null;
    this.markers = [];

    // DOM Elements
    this.sectionEl = document.getElementById('dealer-locator-section');
    this.form = document.getElementById('dealer-search-form');
    this.loadingEl = document.getElementById('dealer-loading');
    this.resultsEl = document.querySelector('.view-content');
    this.headerEl = document.querySelector('.view-header');
    this.mapEl = document.getElementById('styled-google-map');
    this.paginationEl = document.querySelector('.pagination');
    this.newSearchLink = document.getElementById('new-search-link');

    this.init();
  }

  init() {
    this.bindEvents();
    this.checkUrlParams();
  }

  bindEvents() {
    // Form submit
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.currentPage = 1;
      this.search();
    });

    // New search link
    this.newSearchLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.resetToForm();
    });

    // Pagination clicks (delegated)
    this.paginationEl?.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-page]');
      if (link) {
        e.preventDefault();
        const page = parseInt(link.dataset.page);
        if (page) {
          this.currentPage = page;
          this.search();
          // Scroll to top of results
          this.headerEl?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  checkUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const origin = params.get('origin') || params.get('field_geofield_distance[origin]');
    const dealerType = params.get('dealer_type');

    if (origin) {
      const originInput = document.getElementById('edit-field-geofield-distance-origin');
      if (originInput) {
        originInput.value = origin;
        
        // Set dealer type radio if provided
        if (dealerType) {
          const radioToSelect = this.form?.querySelector(`input[name="dealer_type"][value="${dealerType}"]`);
          if (radioToSelect) {
            radioToSelect.checked = true;
          }
        }
        
        // Hide form immediately and show loading
        this.sectionEl?.classList.add('has-results');
        this.showLoading();
        this.search();
      }
    }
  }

  resetToForm() {
    this.sectionEl?.classList.remove('has-results');
    // Clear URL params
    const url = new URL(window.location);
    url.search = '';
    window.history.pushState({}, '', url);
    // Clear map
    if (this.map) {
      this.markers.forEach(m => m.setMap(null));
      this.markers = [];
    }
  }

  async search() {
    const origin = document.getElementById('edit-field-geofield-distance-origin')?.value.trim();
    const distance = parseInt(document.getElementById('edit-field-geofield-distance-distance')?.value) || 200;
    const unit = document.getElementById('edit-field-geofield-distance-unit')?.value || 'miles';
    const dealerType = document.querySelector('#dealer-search-form input[name="dealer_type"]:checked')?.value;

    if (!origin) {
      alert('Please enter a zip code.');
      return;
    }

    this.showLoading();

    try {
      // Geocode the origin
      const coords = await this.geocode(origin + ' North America');

      if (!coords) {
        this.showError('Could not find that location. Please try a different zip code.');
        return;
      }

      this.currentCoords = coords;

      // Update URL with origin and dealer_type
      const url = new URL(window.location);
      url.searchParams.set('origin', origin);
      if (dealerType && dealerType !== 'all') {
        url.searchParams.set('dealer_type', dealerType);
      } else {
        url.searchParams.delete('dealer_type');
      }
      window.history.pushState({}, '', url);

      // Convert to miles if needed
      const radiusMiles = unit === 'km' ? Math.round(distance * 0.621371) : distance;
      const type = dealerType === 'all' ? null : dealerType;

      // Get total count (capped at 30)
      const total = await this.countDealers(coords.lat, coords.lng, radiusMiles, type);
      this.totalResults = Math.min(total, 30);

      // Get dealers
      const offset = (this.currentPage - 1) * this.perPage;
      const dealers = await this.queryDealers(coords.lat, coords.lng, radiusMiles, type, offset);

      // Show results state
      this.sectionEl?.classList.add('has-results');

      // Update header with capped total
      this.updateHeader(origin, this.totalResults);

      // Render results
      this.renderResults(dealers);

      // Render pagination
      this.renderPagination();

      // Update map
      this.updateMap(coords, dealers);

      // Hide loading
      this.loadingEl.style.display = 'none';

    } catch (error) {
      console.error('Search error:', error);
      this.showError('An error occurred. Please try again.');
    }
  }

  async geocode(address) {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${this.googleMapsKey}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        return {
          lat: data.results[0].geometry.location.lat,
          lng: data.results[0].geometry.location.lng
        };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return null;
  }

  async queryDealers(lat, lng, radius, type, offset) {
    const response = await fetch(`${this.supabaseUrl}/rest/v1/rpc/find_dealers_nearby`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': this.supabaseKey,
        'Authorization': `Bearer ${this.supabaseKey}`
      },
      body: JSON.stringify({
        origin_lat: lat,
        origin_lng: lng,
        radius_miles: radius,
        dealer_type: type,
        result_limit: this.perPage,
        result_offset: offset
      })
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async countDealers(lat, lng, radius, type) {
    const response = await fetch(`${this.supabaseUrl}/rest/v1/rpc/count_dealers_nearby`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': this.supabaseKey,
        'Authorization': `Bearer ${this.supabaseKey}`
      },
      body: JSON.stringify({
        origin_lat: lat,
        origin_lng: lng,
        radius_miles: radius,
        dealer_type: type
      })
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  showLoading() {
    if (this.loadingEl) this.loadingEl.style.display = 'block';
  }

  showError(message) {
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    this.sectionEl?.classList.add('has-results');
    if (this.resultsEl) {
      this.resultsEl.innerHTML = `<div class="error-message"><p>${message}</p></div>`;
    }
  }

  updateHeader(origin, total) {
    if (!this.headerEl) return;

    const countEl = this.headerEl.querySelector('.result-count');
    const originEl = this.headerEl.querySelector('.search-origin');

    if (countEl) countEl.textContent = total;
    if (originEl) originEl.textContent = this.escapeHtml(origin);
  }

  renderResults(dealers) {
    if (!this.resultsEl) return;

    if (dealers.length === 0) {
      this.resultsEl.innerHTML = `
        <div class="no-results">
          <p>No dealers found within the specified distance.</p>
          <p>Try increasing your search radius or call <strong>(800) 810-4918</strong>.</p>
        </div>
      `;
      return;
    }

    this.resultsEl.innerHTML = dealers.map((dealer, index) => this.renderDealerCard(dealer, index)).join('');
  }

  renderDealerCard(dealer, index) {
    const mapUrl = this.getDirectionsUrl(dealer);
    const websiteUrl = this.formatWebsite(dealer.website);
    const dealerTypes = this.formatDealerTypes(dealer.dealer_types);
    const rowClass = `views-row views-row-${index + 1} views-row-${index % 2 === 0 ? 'odd' : 'even'}`;

    return `
      <div class="${rowClass}">
        <div class="row">
          <div class="col-xs-12">
            <h3>
              ${websiteUrl
                ? `<a target="_blank" href="${websiteUrl}"><span class="field-content">${this.escapeHtml(dealer.name)}</span><i class="fa fa-share-square-o"></i></a>`
                : `<span class="field-content">${this.escapeHtml(dealer.name)}</span>`
              }
            </h3>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12 col-md-4 dealer-data">
            <i class="fa fa-map-marker pull-left"></i>
            <address>
              <div class="field-content">
                <div class="street-block"><div class="thoroughfare">${this.escapeHtml(dealer.address_line1 || '')}</div></div>
                ${dealer.address_line2 ? `<div class="street-block"><div class="premise">${this.escapeHtml(dealer.address_line2)}</div></div>` : ''}
                <div class="addressfield-container-inline locality-block country-${dealer.country || 'US'}">
                  <span class="locality">${this.escapeHtml(dealer.city || '')}</span>${dealer.state ? `, <span class="state">${this.escapeHtml(dealer.state)}</span>` : ''} <span class="postal-code">${this.escapeHtml(dealer.zip || '')}</span>
                </div>
                <span class="country">${dealer.country === 'CA' ? 'Canada' : 'United States'}</span>
              </div>
            </address>
            <div class="phone">
              <i class="fa fa-phone"></i>${this.escapeHtml(dealer.phone || '')}
            </div>
          </div>
          <div class="col-xs-12 col-md-4 dealer-data">
            ${dealer.installs ? `
              <div class="offers-install">
                <ul class="fa-ul">
                  <li><i class="fa-li fa fa-wrench"></i><span>Offers Installation Service</span></li>
                </ul>
              </div>
            ` : ''}
            ${dealerTypes ? `
              <ul class="fa-ul">
                <li><i class="fa-li fa fa-shopping-cart"></i>${dealerTypes}</li>
              </ul>
            ` : ''}
          </div>
          <div class="col-xs-12 col-md-4 dealer-data">
            <a target="_blank" href="${mapUrl}" class="button button--primary">Get Directions</a>
            <div class="distance">
              <span class="field-content">${dealer.distance_miles} miles from your search</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderPagination() {
    const totalPages = Math.ceil(this.totalResults / this.perPage);

    if (!this.paginationEl || totalPages <= 1) {
      if (this.paginationEl) this.paginationEl.innerHTML = '';
      return;
    }

    let html = '';

    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (i === this.currentPage) {
        html += `<li class="active"><span>${i}</span></li>`;
      } else if (i === 1 || i === totalPages || (i >= this.currentPage - 2 && i <= this.currentPage + 2)) {
        html += `<li><a href="#" data-page="${i}">${i}</a></li>`;
      } else if (i === this.currentPage - 3 || i === this.currentPage + 3) {
        html += `<li class="pager-ellipsis disabled"><span>…</span></li>`;
      }
    }

    // Next
    if (this.currentPage < totalPages) {
      html += `<li class="next"><a href="#" data-page="${this.currentPage + 1}"> › </a></li>`;
    }

    this.paginationEl.innerHTML = html;
  }

  updateMap(center, dealers) {
    // Check if Google Maps is loaded
    if (typeof google === 'undefined' || !google.maps) {
      console.warn('Google Maps not loaded');
      return;
    }

    // Initialize map if needed
    if (!this.map && this.mapEl) {
      this.mapEl.innerHTML = '';
      this.map = new google.maps.Map(this.mapEl, {
        zoom: 9,
        center: { lat: center.lat, lng: center.lng }
      });
    }

    if (!this.map) return;

    // Clear existing markers
    this.markers.forEach(m => m.setMap(null));
    this.markers = [];

    // Set center
    this.map.setCenter({ lat: center.lat, lng: center.lng });

    // Add dealer markers
    const bounds = new google.maps.LatLngBounds();
    bounds.extend({ lat: center.lat, lng: center.lng });

    // Use custom pin icon if available
    const pinIcon = 'https://cdn.shopify.com/s/files/1/0005/5318/9491/files/pin-half.png?v=1768263445';

    dealers.forEach(dealer => {
      if (dealer.latitude && dealer.longitude) {
        const lat = parseFloat(dealer.latitude);
        const lng = parseFloat(dealer.longitude);

        const marker = new google.maps.Marker({
          position: { lat, lng },
          map: this.map,
          icon: pinIcon,
          title: dealer.name
        });

        const infoWindow = new google.maps.InfoWindow({
          content: `<strong>${this.escapeHtml(dealer.name)}</strong><br>${dealer.distance_miles} miles`
        });

        marker.addListener('click', () => {
          infoWindow.open(this.map, marker);
        });

        this.markers.push(marker);
        bounds.extend({ lat, lng });
      }
    });

    // Fit bounds
    if (this.markers.length > 0) {
      this.map.fitBounds(bounds);
    }
  }

  formatAddress(dealer) {
    return [dealer.address_line1, dealer.city, dealer.state, dealer.zip]
      .filter(Boolean)
      .join(', ');
  }

  getDirectionsUrl(dealer) {
    const parts = [
      dealer.address_line1,
      dealer.city,
      dealer.state,
      dealer.zip
    ].filter(Boolean);
    const address = parts.join('+');
    return `https://www.google.com/maps/dir//${encodeURIComponent(address)}`;
  }

  formatWebsite(url) {
    if (!url) return null;
    if (!url.startsWith('http')) return 'https://' + url;
    return url;
  }

  formatDealerTypes(types) {
    if (!types || types.length === 0) return '';
    const labels = {
      gooseneck: 'Gooseneck',
      fifthwheel: 'Fifth Wheel',
      bumper: 'Bumper',
      farmranch: 'Farm & Ranch',
      motorcycle: 'Motorcycle'
    };
    return types.map(t => labels[t] || t).join(', ');
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (window.DEALER_CONFIG && window.DEALER_CONFIG.supabaseUrl) {
    new DealerLocator(window.DEALER_CONFIG);
  } else {
    console.warn('Dealer Locator: Missing configuration. Please set Supabase credentials in theme settings.');
  }
});
